import React, { useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Stack,
  Select,
  TextInput,
  Textarea,
  MultiSelect,
  Group,
  Text,
  Badge
} from '@mantine/core';
import { updateContextData, saveContextData } from '../redux/actions/contextActions';
import debounce from 'lodash/debounce';

function toTitleCase(str) {
  return str
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/\b([Aa]nd)\b/g, '&')
    .split(/\s+/)
    .map((word) => {
      const upper = word.toUpperCase();
      if (['EU', 'ROC', 'AUC'].includes(upper)) {
        return upper;
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const StepContent = ({ modelId, assessmentUuid, section, sectionConfig }) => {
  const dispatch = useDispatch();
  const contextData = useSelector((s) => s.context.data[modelId] || {});
  const sectionData = contextData[section] || {};
  
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  // Debounced save function
  const debouncedSave = useMemo(
    () => debounce((sec, data) => {
      setIsSaving(true);
      dispatch(saveContextData(modelId, assessmentUuid, sec, data))
        .then(() => {
          setIsSaving(false);
          setIsSaved(true);
          setTimeout(() => setIsSaved(false), 2000);
        })
        .catch(() => setIsSaving(false));
    }, 1500),
    [dispatch, modelId, assessmentUuid]
  );

  const handleChange = (fieldName, value) => {
    dispatch(updateContextData(modelId, section, fieldName, value));
    debouncedSave(section, { ...sectionData, [fieldName]: value });
  };


  if (!sectionConfig || !Array.isArray(sectionConfig)) {
    return (
      <Box p="md">
        <Text color="dimmed">No configuration found for {section}</Text>
      </Box>
    );
  }

  // Clean styles now that global text-align: center is removed
  const commonStyles = {
    label: { 
      display: 'block',
      width: '100%'
    },
    description: { 
      display: 'block'
    },
    error: { 
      display: 'block'
    }
  };

  return (
    <Stack spacing="md" sx={{ textAlign: 'left' }}>
      {/* Save Status Badge */}
      <Group position="right" mb="xs">
        <Badge color={isSaving ? 'yellow' : isSaved ? 'green' : 'gray'} variant="light" size="sm">
          {isSaving ? 'Saving…' : isSaved ? 'Saved' : 'Auto-save'}
        </Badge>
      </Group>
      
      {sectionConfig.map((field) => {
        const fieldValue = sectionData[field.name] || '';

        return (
          <Box key={field.name} sx={{ width: '100%' }}>
            {(() => {
              switch (field.type) {
                case 'dropdown':
                  return (
                    <Select
                      label={toTitleCase(field.name)}
                      description={field.tooltip || field.description}
                      placeholder={field.placeholder || `Select ${toTitleCase(field.name)}`}
                      data={field.options || []}
                      value={fieldValue}
                      onChange={(value) => handleChange(field.name, value)}
                      required={field.required}
                      styles={commonStyles}
                    />
                  );

                case 'multiselect':
                  return (
                    <MultiSelect
                      label={toTitleCase(field.name)}
                      description={field.tooltip || field.description}
                      placeholder={field.placeholder || `Select ${toTitleCase(field.name)}`}
                      data={field.options || []}
                      value={Array.isArray(fieldValue) ? fieldValue : []}
                      onChange={(value) => handleChange(field.name, value)}
                      required={field.required}
                      styles={commonStyles}
                    />
                  );

                case 'textarea':
                  return (
                    <Textarea
                      label={toTitleCase(field.name)}
                      description={field.tooltip || field.description}
                      placeholder={field.placeholder || `Enter ${toTitleCase(field.name)}`}
                      value={fieldValue}
                      onChange={(e) => handleChange(field.name, e.currentTarget.value)}
                      minRows={field.minRows || 3}
                      maxRows={field.maxRows || 6}
                      autosize
                      required={field.required}
                      styles={{
                        ...commonStyles,
                        input: { textAlign: 'left', lineHeight: 1.5 }
                      }}
                    />
                  );

                default:
                  return (
                    <TextInput
                      label={toTitleCase(field.name)}
                      description={field.tooltip || field.description}
                      placeholder={field.placeholder || `Enter ${toTitleCase(field.name)}`}
                      value={fieldValue}
                      onChange={(e) => handleChange(field.name, e.currentTarget.value)}
                      required={field.required}
                      styles={commonStyles}
                    />
                  );
              }
            })()}
          </Box>
        );
      })}
    </Stack>
  );
};

export default StepContent;